<template>
  <v-container fluid class="pa-2"> <v-row align="start" class="mb-8">
      <v-col cols="12" md="7">
        <div class="d-flex align-center mb-1">
          <v-icon large color="primary" class="mr-3">mdi-shield-account</v-icon>
          <h1 class="text-h4 font-weight-black white--text">
            Balance <span class="primary--text">Mensual</span>
          </h1>
        </div>
        <p class="text-subtitle-1   text--lighten-2 ml-11" v-if="balance">
          Periodo consultado: 
          <span class="font-weight-bold white--text">{{ moment(balance.desde).format("DD/MM/YYYY") }}</span> al 
          <span class="font-weight-bold white--text">{{ moment(balance.hasta).format("DD/MM/YYYY") }}</span>
        </p>
      </v-col>
      <v-col cols="12" md="5" class="d-flex justify-md-end align-center">
        <v-sheet elevation="2" rounded="xl"  class="pa-2" width="300">
          <div class="caption   text--lighten-3 font-weight-bold ml-2 mb-1 text-uppercase">Cambiar Período</div>
          <FechaPeriodoVFilter v-model="fechaPeriodo" dark />
        </v-sheet>
      </v-col>
    </v-row>

    <v-row v-if="balance">
      <v-col cols="12" md="7">
        <v-card elevation="3"  class="rounded-xl overflow-hidden fill-height border-0" dark>
          <v-toolbar flat >
            <v-icon left color="primary">mdi-swap-vertical-circle</v-icon>
            <v-toolbar-title class="font-weight-bold">Flujo del Período</v-toolbar-title>
          </v-toolbar>
          
          <v-card-text class="pa-8">
            <v-row class="mb-8">
              <v-col cols="6">
                <div class="d-flex align-center mb-1">
                  <v-avatar color="success darken-4" size="32" class="mr-2">
                    <v-icon color="success lighten-2" small>mdi-arrow-up</v-icon>
                  </v-avatar>
                  <span class="text-overline success--text text--lighten-2 font-weight-black">INGRESOS</span>
                </div>
                <span class="text-h5 font-weight-bold white--text">
                  {{ balance.resultado_ejercicio.ingresos | convertPesoArg }}
                </span>
              </v-col>
              <v-col cols="6" class="text-right">
                <div class="d-flex align-center justify-end mb-1">
                  <span class="text-overline error--text text--lighten-2 font-weight-black mr-2">EGRESOS</span>
                  <v-avatar color="error darken-4" size="32">
                    <v-icon color="error lighten-2" small>mdi-arrow-down</v-icon>
                  </v-avatar>
                </div>
                <span class="text-h5 font-weight-bold white--text">
                  {{ balance.resultado_ejercicio.egresos | convertPesoArg }}
                </span>
              </v-col>
            </v-row>

            <v-sheet 
              :color="balance.resultado_ejercicio.resultado >= 0 ? 'success darken-3' : 'error darken-3'" 
              rounded="xl" 
              class="pa-6 d-flex justify-space-between align-center elevation-4 border-all"
            >
              <div>
                <div class="text-overline white--text opacity-70">RESULTADO NETO</div>
                <div class="text-h5 font-weight-black white--text">
                  {{ balance.resultado_ejercicio.resultado | convertPesoArg }}
                </div>
              </div>
              <v-icon size="48" color="white" class="opacity-30">
                {{ balance.resultado_ejercicio.resultado >= 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}
              </v-icon>
            </v-sheet>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card elevation="3"  dark class="rounded-xl fill-height border-left-highlight">
          <v-card-title class="pa-6 d-flex justify-space-between align-center">
            <span class="font-weight-bold">Conciliación Contable</span>
            <v-tooltip bottom v-if="verificacionPatrimonial">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" :color="verificacionPatrimonial.esExacto ? 'success' : 'amber'">
                  {{ verificacionPatrimonial.esExacto ? 'mdi-check-decagram' : 'mdi-alert-octagon' }}
                </v-icon>
              </template>
              <span>{{ verificacionPatrimonial.esExacto ? 'Saldos conciliados' : 'Diferencia de arrastre detectada' }}</span>
            </v-tooltip>
          </v-card-title>
          
          <v-card-text class="pa-6 pt-0">
            <div class="d-flex justify-space-between mb-2">
              <span class="  text--lighten-3">Activos (Saldo Actual)</span>
              <span class="white--text font-weight-bold">{{ balance.patrimonio_neto.activo | convertPesoArg }}</span>
            </div>
            <div class="d-flex justify-space-between mb-2">
              <span class="  text--lighten-3">Pasivos (Saldo Actual)</span>
              <span class="white--text font-weight-bold">{{ balance.patrimonio_neto.pasivo | convertPesoArg }}</span>
            </div>
            <div class="d-flex justify-space-between mb-4 success--text text--lighten-3">
              <span class="caption">Resultado del Mes</span>
              <span class="caption font-weight-bold">+ {{ balance.resultado_ejercicio.resultado | convertPesoArg }}</span>
            </div>
            
            <v-divider class="my-4 blue-grey"></v-divider>

            <div class="d-flex justify-space-between mb-2 amber--text text--lighten-4">
              <span class="caption">Arrastre / Apertura (PN Anterior)</span>
              <span class="caption">{{ (balance.patrimonio_neto.patrimonio_neto - balance.resultado_ejercicio.resultado) | convertPesoArg }}</span>
            </div>

            <div v-if="verificacionPatrimonial && !verificacionPatrimonial.esExacto" class="amber--text text--lighten-2 caption font-italic mb-4">
              * Nota: Desfase de apertura detectado: {{ verificacionPatrimonial.diferencia | convertPesoArg }}
            </div>

            <v-sheet  rounded="lg" class="pa-4 text-center mt-4">
              <span class="text-overline primary--text">VALOR CONTABLE PN</span>
              <div class="text-h4 white--text font-weight-black">
                {{ balance.patrimonio_neto.patrimonio_neto | convertPesoArg }}
              </div>
            </v-sheet>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" class="mt-6">
        <v-card elevation="2"  dark class="rounded-xl overflow-hidden">
          <v-toolbar flat >
            <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
            <v-toolbar-title class="text-subtitle-1 font-weight-bold">Detalle de Cuentas del Balance</v-toolbar-title>
          </v-toolbar>
          
          <div class="px-6 py-4 d-flex font-weight-black   text--lighten-3 border-bottom text-caption text-uppercase">
            <div style="flex: 2;">Cuenta Contable</div>
            <div style="flex: 1;" class="text-right">Anterior (Apertura)</div>
            <div style="flex: 1;" class="text-right px-4">Mov. Mes</div>
            <div style="flex: 1;" class="text-right">Saldo Final</div>
          </div>

          <v-list class="pa-4 ">
            <BalanceMensualItem
              v-for="cuenta in balance.cuentas"
              :key="cuenta.id"
              :cuenta="cuenta"
              :depth="0"
              class="account-item-row"
            />
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<style scoped>
.account-item-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}
.border-left-highlight {
  border-left: 4px solid #1976D2 !important; /* Ajusta a tu color primario */
}
.opacity-30 { opacity: 0.3; }
.opacity-70 { opacity: 0.7; }
</style>
<script>
import BalanceMensualItem from "@/views/empresa/contabilidad/reporte/balance/mensual/BalanceMensualItem.vue";
import FechaPeriodoVFilter from "@/components/util/FechaPeriodoVFilter.vue";

export default {
  name: "ReporteCuentaContableAcumulado",
  components: {
    BalanceMensualItem,
    FechaPeriodoVFilter,
  },
  data() {
    return {
      fechaPeriodo: new Date().toISOString().substr(0, 7),
      error: null,
      balance: null,
      loading: false,
    };
  },
  computed: {
    verificacionPatrimonial() {
      // Verificamos que el objeto balance y sus hijos existan para evitar errores de renderizado inicial
      if (!this.balance?.patrimonio_neto || !this.balance?.resultado_ejercicio) return null;

      const { activo, pasivo, patrimonio_neto } = this.balance.patrimonio_neto;
      const resultadoMes = this.balance.resultado_ejercicio.resultado;

      // La ecuación contable: (Activo + Pasivo) + Resultado Neto. 
      // Nota: El pasivo se suma porque el servidor ya lo envía negativo.
      const calculado = (activo + pasivo) + resultadoMes;
      const diferencia = patrimonio_neto - calculado;

      return {
        calculado,
        diferencia: Math.abs(diferencia),
        esExacto: Math.abs(diferencia) < 0.05 // Tolerancia de 5 centavos
      };
    }
  },
  methods: {
    fetchDetalleCuenta() {
      this.loading = true;
      this.error = null;
      const url = `/reportes/balance-mensual?periodo=${this.fechaPeriodo}`;

      this.axios
        .get(url)
        .then((response) => {
          this.balance = response.data;
        })
        .catch((error) => {
          this.error = error.message || "Error al cargar los datos";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    currency(value) {
      if (typeof value !== "number") return "-";
      return new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
        minimumFractionDigits: 2,
      }).format(value);
    },
  },
  watch: {
    fechaPeriodo() {
      this.fetchDetalleCuenta();
    },
  },
  created() {
    this.fetchDetalleCuenta();
  },
};
</script>

